<template>
  <b-modal v-model="showModal"
           @ok="performCreditCardPayment"
           :ok-disabled="isUpdatingStatus">
    <div slot="modal-title">
      {{ $t('message.confirm_order_payment.credit_card_payment') }}
    </div>
    <section>
      <order-summary-total-short :order="order"/>
      <hr>
      <div v-if="payment && !isChangingNewCard">
        <label>{{ $t('message.my_reservations.payment') }}</label>
        <payment-small :payment="payment" class="mt-3"/>
      </div>
      <div v-else>
        <payment-form ref="paymentForm" :paymentData="paymentData"/>
      </div>
      <div class="text-center mt-3" v-if="displayLoader">
        <img src="/static/img/ring.gif" width="60"> {{ $t('message.button.loading') }}
      </div>
    </section>
    <div slot="modal-cancel" @click="showModal = false">{{ $t('message.button.close') }}</div>
    <div slot="modal-ok">{{ (isLoading || isUpdating) ? $t('message.button.loading') : $t('message.button.submit') }}</div>
  </b-modal>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import OrderSummaryTotalShort from './OrderSummaryTotalShort'
  import PaymentSmall from '../../payment/PaymentSmall'
  import PaymentForm from '../../payment/PaymentForm'
  import axios from 'axios'

  export default {
    props: ['order'],
    components: {
      OrderSummaryTotalShort,
      PaymentSmall,
      PaymentForm
    },
    data () {
      return {
        showModal: false,
        displayLoader: false,
        isChangingNewCard: false,
        paymentData: {
          name: null,
          number: null,
          expirationDate: '',
          securityCode: null,
          savePayment: true,
          displaySavePayment: true,
          isDefault: false
        },
        omiseTokens: {
          secureToken: null,
          token: null
        },
        isLoading: false
      }
    },
    computed: {
      ...mapGetters({
        globalState: 'globalVal/getState',
        state: 'orderDetail/getState',
        user: 'auth/getUser',
        payment: 'payment/getPayment',
        isSaving: 'payment/isSaving',
        isUpdating: 'orderDetail/getIsUpdatingStatus',
        isUpdatingStatus: 'orderDetail/getIsUpdatingStatus'
      }),
      ...mapState('payment', [
        'isSavingNewCard'
      ])
    },
    watch: {
      'omiseTokens.token': function (newVal, oldVal) {
        this.isLoading = false
        if (this.omiseTokens.secureToken && this.omiseTokens.token) {
          const returnUri = location.href.split('?')[0] + '?tmpToken=' + this.omiseTokens.token + '&action=professional_booking_payment'
          const submitData = {
            user_id: this.user.id,
            token: this.omiseTokens.secureToken,
            return_uri: returnUri,
            omise_account: 'hellobeneat'
          }
          this.checkOmiseSecure(submitData)
        }
      },
      // Check saving additional hours
      async isUpdating () {
        if (!this.state.isUpdating) {
          this.displayLoader = false
          this.showCreditCardPaymentModal(false)
          await this.loadOrderDetail(this.order.id)
        }
      },
      async isSavingNewCard () {
        if (!this.isSavingNewCard) {
          // If change new card success close modal
          await this.loadOrderDetail(this.order.id)
          this.showModal = false
        }
      }
    },
    methods: {
      ...mapActions('orderDetail', ['loadOrderDetail']),
      checkOmiseSecure (submitData) {
        const url = 'payments/' + this.user.id + '/check-omise-secure'
        axios.post(url, submitData)
          .then(res => {
            this.isLoading = false
            const response = res.data
            if (!response.error) {
              this.showCreditCardPaymentModal(false)
              this.$store.commit('payment/SET_TMP_OMISE_CHARGE_ID', response.omise_charge_id)
              window.location = response.authorize_uri
            } else {
              this.$swal({text: response.message, type: 'error'})
            }
          })
          .catch(error => {
            this.showCreditCardPaymentModal(false)
            console.log(error)
          })
      },
      setChangingNewCard (status) {
        this.isChangingNewCard = status
      },
      showCreditCardPaymentModal (status) {
        this.showModal = status
        if (status) {
          this.$store.dispatch('payment/LOAD_PAYMENT_DETAIL', this.user.id)
        }
      },
      performCreditCardPayment (evt) {
        // Prevent modal from closing
        evt.preventDefault()
        // Check payment of user
        if (this.payment === null || this.isChangingNewCard) {
          if (this.$refs.paymentForm.paymentFormValidation()) {
            this.isLoading = true
            // Prepare card data
            const cardNumber = this.paymentData.number.replace(/\s+/g, '')
            let arrExpiration = this.paymentData.expirationDate.split('/')
            const expirationMonth = arrExpiration[0]
            const expirationYear = 2000 + parseInt(arrExpiration[1])

            const card = {
              'name': this.paymentData.name,
              'number': cardNumber,
              'expiration_month': expirationMonth,
              'expiration_year': expirationYear,
              'security_code': this.paymentData.securityCode
            }

            // Perform omise process
            this.omiseTokens.secureToken = null
            this.omiseTokens.token = null
            // Get token from omise secure for otp
            window.Omise.setPublicKey(this.globalState.omisePublicKey)
            window.Omise.createToken('card', card, (statusCode, response) => {
              if (statusCode === 200 && response.object === 'token') {
                const omiseToken = response.id
                this.omiseTokens.secureToken = omiseToken
                // Get token from omise non-secure for adding omise customer id
                window.Omise.setPublicKey(this.globalState.omisePublicKey)
                window.Omise.createToken('card', card, (statusCode, response) => {
                  if (statusCode === 200 && response.object === 'token') {
                    const omiseToken = response.id
                    this.omiseTokens.token = omiseToken
                    this.$store.commit('payment/SET_TMP_OMISE_TOKEN', omiseToken)
                  } else {
                    const alertText = 'Error: ' + response.message
                    this.$swal({text: alertText})
                    this.isLoading = false
                  }
                })
              } else {
                const alertText = 'Error: ' + response.message
                this.$swal({text: alertText})
                this.isLoading = false
              }
            })
          }
        } else {
          this.addCreditCardPayment('')
        }
      },
      addCreditCardPayment (omiseToken) {
        // Add credit card payment
        const submitData = {
          order_id: this.order.id,
          user_id: this.user.id,
          omise_token: omiseToken
        }
        this.$store.dispatch('orderDetail/ADD_CREDIT_CARD_PAYMENT', submitData)
      },
      saveNewCard (omiseToken) {
        // Change new card
        const submitData = {
          order_id: this.order.id,
          user_id: this.user.id,
          omise_token: omiseToken
        }
        this.$store.dispatch('payment/SAVE_NEW_CARD', submitData)
      }
    },
    mounted () {
      this.$store.commit('orderDetail/SET_UPDATE_DATA', false)
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/order/components/CreditCardPaymentModal.vue